// =============================================================================
// admin-activation-alerts-client.http.ts — Adaptateur HTTP RÉEL de l'alerte 48h (§2.2 l.728).
// =============================================================================
// Câble GET /admin/activation-alerts derrière le MÊME DI seam (AdminActivationAlertsClientProvider) :
// l'écran consomme l'interface AdminActivationAlertsClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, calcul AU VOL). Cet adaptateur ne porte AUCUNE règle
// métier : il parse la réponse et mappe l'erreur. La route est un GET (admin LIT, jamais d'écriture —
// invariant n°1) ⇒ AUCUN en-tête CSRF (le double-submit admin_csrf ne couvre que les mutations).
//
// SÉCURITÉ : la session admin est portée par le COOKIE httpOnly posé par l'API (credentials:
// 'include'). L'UI ne lit ni n'écrit le secret de session. Le 401 est mappé en erreur typée LOCALE
// (pas d'intercepteur global → landing : L-2 différé V1.5, DEC-20260721-6). Aucune donnée en
// localStorage (RULE_REF_006).

import {
  AdminActivationAlertsApiError,
  type AdminActivationAlertsClient,
  type PendingActivationAccountsDto,
  type StalePendingActivationsDto,
} from '@/api/admin-activation-alerts-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin activation-alerts. `baseUrl` par défaut `/api`
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En test,
 * on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpAdminActivationAlertsClient
  implements AdminActivationAlertsClient
{
  public constructor(private readonly baseUrl: string = '/api') {}

  public async getStalePendingActivations(): Promise<StalePendingActivationsDto> {
    const response = await fetch(`${this.baseUrl}/admin/activation-alerts`, {
      method: 'GET',
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as StalePendingActivationsDto;
  }

  public async getPendingActivationAccounts(): Promise<PendingActivationAccountsDto> {
    // §2.5 l.1085 (item 7) — GET nominatif (admin LIT, jamais d'écriture) : cookie httpOnly, aucun CSRF.
    const response = await fetch(
      `${this.baseUrl}/admin/activation-alerts/pending-accounts`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as PendingActivationAccountsDto;
  }

  /** Convertit une réponse non-OK (401 inclus) en erreur applicative typée (message serveur tel quel). */
  private async toApiError(
    response: Response,
  ): Promise<AdminActivationAlertsApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur les alertes d'activation.`;
    return new AdminActivationAlertsApiError(message, response.status);
  }
}
